import { Injectable, computed, inject } from '@angular/core';
import { TokenStore } from 'eshava-shared';

import { Permission, permissionsOf } from './permissions';

/**
 * What the signed-in user may do here.
 *
 * **Derived from the session, not fetched.** The permission string arrives in the app metadata of the
 * userinfo response, which the package already carries through untouched. So this decodes rather than
 * asks -- and it follows the session: a renewal that brings a changed set changes what is on screen
 * without anything having to be reloaded.
 *
 * **A claim the browser can edit is not a security boundary**, and nothing here pretends otherwise.
 * Every endpoint checks for itself; what this does is stop offering what would be refused. The two
 * are different jobs -- one protects the data, the other stops the interface lying about what is
 * available.
 *
 * **There is no permission that grants the portal as a whole.** Access is decided per area and per
 * screen; someone with none of them sees an empty sidebar and the refusal page, and that is the
 * correct answer rather than a special case.
 */
@Injectable({ providedIn: 'root' })
export class PermissionsService {
  private readonly tokens = inject(TokenStore);

  private readonly granted = computed(() => permissionsOf(this.tokens.claims()?.permissionString));

  has(permission: Permission): boolean {
    return this.granted().has(permission);
  }

  /**
   * Whether every one of them is granted.
   *
   * **All, not any.** A screen inside an area needs the module and the screen: someone with the
   * licence list permission but not the licences module has been given something that does not
   * apply, and offering it would be reading the grant more generously than it was made.
   */
  hasAll(permissions: readonly Permission[]): boolean {
    return permissions.every(permission => this.granted().has(permission));
  }
}